/**
 * AURORA DS — paleta wykresów (literalne hexy).
 *
 * DLACZEGO OSOBNY MODUŁ, A NIE TOKENY CSS.
 * Biblioteki wykresów rysują po `<canvas>` (Chart.js, nasze płótna Living
 * Network) albo przyjmują kolor jako wartość propsa, którą przekazują dalej do
 * kontekstu rysowania — a tam `var(--ids-*)` nie ma czego rozwiązać, bo nie ma
 * kaskady. Recharts w wielu miejscach zadziała z `var()` (atrybuty prezentacyjne
 * SVG są mapowane na właściwości CSS), ale nie wszędzie — `cursor`, gradienty
 * `<linearGradient>` i własne tooltipy dostają te wartości jako zwykłe stringi.
 * Dlatego wykresy potrzebują wartości DOSŁOWNYCH, a nie tokenów.
 *
 * To sprawia, że wykresy są jedynym miejscem w systemie, w którym hex jest
 * poprawny. Problem był w tym, że każdy moduł trzymał własną kopię: ten sam
 * blok pięciu stałych był skopiowany do `module-neurosense` (3 pliki) i
 * `module-waterandice` (3 pliki) z identycznymi wartościami, a
 * `module-conspectus` i `module-agenda` miały swoje własne — siatkę `#374151`
 * i opisy osi `#6b7280` (domyślne szarości Tailwinda, nie Cosmos). Efekt był
 * widoczny: chrom wykresu — siatka, osie, tło tooltipa — wyglądał inaczej w
 * zależności od modułu, choć wykres pełnił tę samą rolę.
 *
 * Wartości poniżej to Cosmos i akcenty Aurory z `packages/ui/src/styles/aurora.css`.
 * Zmiana palety wykresów w systemie = zmiana TEGO pliku.
 *
 * TRYB JASNY. Stałe `CHART_*` bez sufiksu są skalibrowane pod `data-mode="dark"`
 * (domyślny w systemie) i ZOSTAJĄ takie — używa ich każde płótno leżące na scenie
 * na stałe ciemnej. Wariant jasny nie może być wybrany w CSS, bo — jak wyżej —
 * `var()` nie ma tu czego rozwiązać; wybiera się go w JS, funkcjami
 * `chartChrome(mode)` / `chartSeries(mode)` / `chartStatus(mode)`, albo prościej
 * hookiem `useChartTheme()` z `@insolitum/ui/hooks/useChartTheme`, który sam
 * czyta `data-mode` z `<html>` i przerysowuje wykres po przełączeniu motywu.
 */

/** Tryb, pod który dobierana jest paleta. Ten sam zbiór, co `data-mode`. */
export type ChartMode = 'dark' | 'light';

/* ── Chrom wykresu ─────────────────────────────────────────────────────────── */

/** Siatka (`CartesianGrid`) — ledwo widoczna, ma nie konkurować z serią. */
export const CHART_GRID = '#232E45'; // --cosmos-700

/** Linia osi (`axisLine`) — o stopień mocniejsza od siatki. */
export const CHART_AXIS = '#475569'; // --cosmos-500

/** Opisy podziałek (`tick.fill`) — kontrast AA na tle strony. */
export const CHART_TICK = '#94A3B8'; // --cosmos-300

/** Tło tooltipa — powierzchnia podniesiona, żeby odcinała się od karty. */
export const CHART_TOOLTIP_BG = '#141B2D'; // --cosmos-800

/** Obramowanie tooltipa — to samo, co obramowanie karty. */
export const CHART_TOOLTIP_BORDER = '#232E45'; // --cosmos-700

/** Tekst pierwszoplanowy w tooltipie i legendzie. */
export const CHART_TEXT = '#F8FAFC'; // --ids-text

/**
 * Gotowy `contentStyle` dla `<Tooltip>` Rechartsa — jeden zestaw na system,
 * bo tooltip składany ręcznie w każdym module za każdym razem wychodził inny
 * (inny promień, inny cień, inne tło).
 */
export const CHART_TOOLTIP_STYLE = {
  backgroundColor: CHART_TOOLTIP_BG,
  border: `1px solid ${CHART_TOOLTIP_BORDER}`,
  borderRadius: '10px', // rounded-ids-md
  color: CHART_TEXT,
} as const;

/* ── Serie ────────────────────────────────────────────────────────────────── */

/**
 * Kolejność serii. Pierwsze trzy to gradient marki (`--ids-grad-a/b/c`), więc
 * wykres z jedną, dwiema lub trzema seriami od razu wygląda „insolitumowo”.
 *
 * Czerwień jest OSTATNIA celowo: w całym systemie znaczy błąd/alarm
 * (`--ids-danger`), więc nie może trafić na drugą serię neutralnego wykresu i
 * sugerować problemu tam, gdzie go nie ma. Kto potrzebuje semantyki stanu,
 * bierze `CHART_STATUS`, nie tę tablicę.
 */
export const CHART_SERIES: readonly string[] = [
  '#3B82F6', // --blue-500    / --ids-brand
  '#22D3EE', // --cyan-400    / --ids-grad-a
  '#8B5CF6', // --violet-500  / --ids-grad-c
  '#10B981', // --emerald-500
  '#F59E0B', // --amber-500   / --ids-warning
  '#EC4899', // --pink-500
  '#14B8A6', // --teal-500
  '#F97316', // --orange-500
  '#6366F1', // --indigo-500
  '#EF4444', // --red-500     / --ids-danger
];

/** Kolor n-tej serii; zawija się, więc wykres o dowolnej liczbie serii działa. */
export function chartColor(index: number): string {
  return CHART_SERIES[((index % CHART_SERIES.length) + CHART_SERIES.length) % CHART_SERIES.length];
}

/**
 * Semantyka stanu — te same trzy kolory, co `.pill--ok/--warn/--bad` i tokeny
 * `--ids-success/warning/danger`, żeby „zielony” na wykresie i „zielony” na
 * plakietce nad wykresem był tym samym zielonym.
 */
export const CHART_STATUS = {
  ok: '#22C55E', // --ids-success
  warn: '#F59E0B', // --ids-warning
  bad: '#EF4444', // --ids-danger
  info: '#3B82F6', // --ids-info
  idle: '#64748B', // --cosmos-400 — brak danych / wyłączone
} as const;

/* ── WARIANT JASNY ────────────────────────────────────────────────────────────
   Nie jest odbiciem ciemnego. Na białej karcie:
   · chrom musi PRZYCIEMNIĆ się, nie rozjaśnić — siatka #E2E8F0, ticki #64748B;
   · akcenty schodzą z kotwicy 500 na 600, bo 500 na białym tle traci kontrast
     (żółty i cyan są tu najgorsze — #F59E0B na białym to 2,1:1);
   · semantyka stanu bierze te same wartości, co blok `[data-mode="light"]`
     w aurora.css, żeby „zielony” na wykresie i na plakietce nad wykresem był
     tym samym zielonym w OBU trybach.
   ─────────────────────────────────────────────────────────────────────────── */

export interface ChartChrome {
  grid: string;
  axis: string;
  tick: string;
  tooltipBg: string;
  tooltipBorder: string;
  text: string;
}

const CHROME_DARK: ChartChrome = {
  grid: CHART_GRID,
  axis: CHART_AXIS,
  tick: CHART_TICK,
  tooltipBg: CHART_TOOLTIP_BG,
  tooltipBorder: CHART_TOOLTIP_BORDER,
  text: CHART_TEXT,
};

const CHROME_LIGHT: ChartChrome = {
  grid: '#E2E8F0', // --paper-200
  axis: '#CBD5E1', // --paper-300
  tick: '#64748B', // --paper-500 — 4,8:1 na #FFFFFF
  tooltipBg: '#FFFFFF', // --paper-0 (--ids-surface-raised w light)
  tooltipBorder: '#CBD5E1', // --paper-300 (--ids-border-strong w light)
  text: '#0F172A', // --paper-900 (--ids-text w light)
};

/** Chrom wykresu dla danego trybu — siatka, osie, ticki, tooltip, tekst. */
export function chartChrome(mode: ChartMode): ChartChrome {
  return mode === 'light' ? CHROME_LIGHT : CHROME_DARK;
}

/** `contentStyle` tooltipa Rechartsa dla danego trybu. */
export function chartTooltipStyle(mode: ChartMode) {
  const c = chartChrome(mode);
  return {
    backgroundColor: c.tooltipBg,
    border: `1px solid ${c.tooltipBorder}`,
    borderRadius: '10px', // rounded-ids-md
    color: c.text,
  };
}

/** Serie dla trybu jasnego — kotwica 600 zamiast 500, ta sama KOLEJNOŚĆ. */
export const CHART_SERIES_LIGHT: readonly string[] = [
  '#2563EB', // --blue-600
  '#0891B2', // --cyan-600
  '#7C3AED', // --violet-600
  '#059669', // --emerald-600
  '#D97706', // --amber-600
  '#DB2777', // --pink-600
  '#0D9488', // --teal-600
  '#EA580C', // --orange-600
  '#4F46E5', // --indigo-600
  '#DC2626', // --red-600 — czerwień ostatnia, jak w ciemnym
];

/** Serie dla danego trybu. */
export function chartSeries(mode: ChartMode): readonly string[] {
  return mode === 'light' ? CHART_SERIES_LIGHT : CHART_SERIES;
}

/** Kolor n-tej serii w danym trybie; zawija się jak `chartColor`. */
export function chartColorFor(mode: ChartMode, index: number): string {
  const s = chartSeries(mode);
  return s[((index % s.length) + s.length) % s.length];
}

export interface ChartStatus {
  ok: string;
  warn: string;
  bad: string;
  info: string;
  idle: string;
}

/** Semantyka stanu dla trybu jasnego — wartości z bloku `[data-mode="light"]`. */
export const CHART_STATUS_LIGHT: ChartStatus = {
  ok: '#16A34A', // --ids-success (light)
  warn: '#D97706', // --ids-warning (light)
  bad: '#DC2626', // --ids-danger (light)
  info: '#2563EB', // --ids-info (light)
  idle: '#94A3B8', // neutral wyciszony na białym
};

/** Semantyka stanu dla danego trybu. */
export function chartStatus(mode: ChartMode): ChartStatus {
  return mode === 'light' ? CHART_STATUS_LIGHT : CHART_STATUS;
}

/* ── Próbniki koloru dla użytkownika ──────────────────────────────────────── */

/**
 * Swatche w polach „wybierz kolor kategorii”. Użytkownik trafia na to pole w
 * dwóch miejscach (kategorie Agendy i kategorie w panelu administratora) —
 * wcześniej każde oferowało inną listę, w tym kolory spoza palety
 * (`#eab308` żółty Tailwinda, `#1f2937` i `#6b7280` z domyślnej szarości).
 *
 * Kolejność: seria marki, potem pozostałe akcenty, na końcu neutral — czyli
 * pierwszy swatch jest zawsze rozsądnym domyślnym wyborem.
 */
export const CATEGORY_SWATCHES: readonly string[] = [
  ...CHART_SERIES,
  '#A855F7', // --purple-500
  '#0EA5E9', // --sky-500
  '#84CC16', // --lime-500
  '#F43F5E', // --rose-500
  '#64748B', // --cosmos-400 — „bez kategorii”
];
